<script setup lang="ts">
/**
 * Contact Page — Microsoft Bookings Embed
 * Route: /contact/
 * Task: 113-111-T005
 *
 * Renders BookingsEmbed with Osano-gated GA4 analytics:
 * - page_view on mount
 * - bookings_embed_status when embed loads or fails
 * - bookings_fallback_triggered when fallback is shown
 */
import { hasAnalyticsConsent } from '../../../composables/useAnalyticsConsent'

const PAGE_PATH = '/contact/'
const PAGE_TITLE = 'Book an Appointment | QxAIOS'
const PAGE_DESCRIPTION = 'Schedule a booking appointment with the QxAIOS team using our online booking form.'
const CANONICAL_URL = 'https://qx.qiksolve.com/contact/'
const SOCIAL_IMAGE = 'https://qx.qiksolve.com/images/logo.svg'

useSeoMeta({
  title: PAGE_TITLE,
  description: PAGE_DESCRIPTION,
  ogTitle: PAGE_TITLE,
  ogDescription: PAGE_DESCRIPTION,
  ogType: 'website',
  ogUrl: CANONICAL_URL,
  ogImage: SOCIAL_IMAGE,
  twitterCard: 'summary_large_image',
  twitterTitle: PAGE_TITLE,
  twitterDescription: PAGE_DESCRIPTION,
  twitterImage: SOCIAL_IMAGE,
  canonical: CANONICAL_URL,
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: CANONICAL_URL,
    },
  ],
})

const { emitPageView, emitBookingsEmbedStatus, emitBookingsFallbackTriggered } = useAnalytics()

onMounted(() => {
  if (hasAnalyticsConsent()) {
    emitPageView(PAGE_PATH, PAGE_TITLE)
  }
})

function handleEmbedLoaded(): void {
  if (hasAnalyticsConsent()) {
    emitBookingsEmbedStatus('loaded')
  }
}

function isTimeoutEmbedFailure(reason: string): boolean {
  const normalizedReason = reason.trim().toLowerCase()
  return normalizedReason.includes('timeout') || normalizedReason.includes('timed out')
}

function handleEmbedFailed(reason: string): void {
  if (hasAnalyticsConsent()) {
    const status = isTimeoutEmbedFailure(reason) ? 'timeout' : 'failed'
    emitBookingsEmbedStatus(status)
    emitBookingsFallbackTriggered(reason)
  }
}
</script>

<template>
  <UContainer class="py-12">
    <div class="max-w-3xl mx-auto space-y-8">
      <div class="text-center space-y-4">
        <h1 class="text-4xl font-bold text-neutral-900">
          Book an Appointment
        </h1>
        <p class="text-lg text-neutral-600">
          Schedule time with the QxAIOS team to discuss how we can help your organisation.
        </p>
      </div>
      <BookingsEmbed
        @loaded="handleEmbedLoaded"
        @failed="handleEmbedFailed"
      />
      <p class="text-sm text-neutral-600">
        By submitting any contact details through our booking form, you consent to us using that information to respond.
        See our
        <NuxtLink
          to="/privacy"
          class="underline"
        >
          Privacy Policy
        </NuxtLink>.
      </p>
    </div>
  </UContainer>
</template>
